import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { trackSermonPlay } from "@/lib/analytics";

export default function SermonDetail() {
  const { slug } = useParams();
  const [sermon, setSermon] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setSermon(null); setNotFound(false); setPlaying(false);
    api.get(`/sermons/${slug}`).then(({ data }) => setSermon(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="bg-cream py-32 text-center">
        <h1 className="font-display text-4xl font-800 text-navy">Sermon not found.</h1>
        <Link to="/sermons" data-testid="sermon-back-link" className="mt-6 inline-flex items-center gap-2 text-victory font-semibold">
          <ArrowLeft size={16} /> Back to all sermons
        </Link>
      </section>
    );
  }
  if (!sermon) return <section className="bg-cream py-32 text-center text-slate-500">Loading...</section>;

  const videoJsonLd = sermon.youtube_id ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: sermon.title,
    description: sermon.description || sermon.title,
    thumbnailUrl: `https://i.ytimg.com/vi/${sermon.youtube_id}/hqdefault.jpg`,
    uploadDate: sermon.date,
    embedUrl: `https://www.youtube.com/embed/${sermon.youtube_id}`,
  } : null;

  return (
    <>
      <SEO
        title={sermon.title}
        description={sermon.description || `${sermon.title} - a message from Victory Chapel, Clay County FL.`}
        path={`/sermons/${sermon.slug}`}
        jsonLd={videoJsonLd}
      />
      <section className="bg-navy py-14 text-white sm:py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Link to="/sermons" data-testid="sermon-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-white">
            <ArrowLeft size={16} /> All Sermons
          </Link>
          {sermon.series && <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-rose-300">{sermon.series}</p>}
          <h1 className="mt-3 font-display text-4xl font-900 tracking-tight sm:text-5xl text-balance" data-testid="sermon-title">{sermon.title}</h1>
          <p className="mt-4 text-sm text-white/70">
            {sermon.speaker}{sermon.speaker && sermon.date ? "  -  " : ""}{sermon.date}
            {sermon.scripture && <span className="ml-3 font-serif italic text-white/80">{sermon.scripture}</span>}
          </p>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-3xl bg-navy shadow-lift">
              {sermon.youtube_id ? (
                playing ? (
                  <iframe
                    title={`${sermon.title} - sermon video`}
                    src={`https://www.youtube-nocookie.com/embed/${sermon.youtube_id}?autoplay=1&rel=0`}
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="sermon-video"
                  />
                ) : (
                  <button type="button" onClick={() => { setPlaying(true); trackSermonPlay(sermon.title); }}
                          data-testid="sermon-play-button" aria-label={`Play ${sermon.title}`}
                          className="group relative block aspect-video w-full">
                    <img src={`https://i.ytimg.com/vi/${sermon.youtube_id}/maxresdefault.jpg`} alt="" className="h-full w-full object-cover" loading="lazy" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-victory text-white shadow-lift transition-transform duration-300 group-hover:scale-110">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                      </span>
                    </span>
                  </button>
                )
              ) : (
                <div className="flex aspect-video items-center justify-center text-white/50">
                  <p className="font-display text-lg font-700">Video coming soon</p>
                </div>
              )}
            </div>
          </Reveal>
          {sermon.description && (
            <Reveal delay={0.1}>
              <p className="mt-10 max-w-3xl text-lg leading-relaxed text-slate-700" data-testid="sermon-description">{sermon.description}</p>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
